import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FileText,
  Download,
  X,
  Image as ImageIcon,
  Video,
  Music,
  File,
  Maximize2
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

interface AttachmentPreviewProps {
  attachment: {
    id?: string;
    file_name: string;
    file_type: string;
    file_size: number;
    mime_type?: string;
    storage_url?: string;
    width?: number;
    height?: number;
    duration?: number;
  };
  onRemove?: () => void;
  showRemove?: boolean;
}

export default function AttachmentPreview({
  attachment,
  onRemove,
  showRemove = false
}: AttachmentPreviewProps) {
  const { theme } = useTheme();
  const [showModal, setShowModal] = useState(false);

  // Debug logging
  console.log('📎 AttachmentPreview:', {
    file_name: attachment.file_name,
    file_type: attachment.file_type,
    mime_type: attachment.mime_type,
    storage_url: attachment.storage_url,
    has_url: !!attachment.storage_url
  });

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = () => {
    const type = attachment.mime_type || attachment.file_type;
    if (type.startsWith('image/')) return <ImageIcon className="h-5 w-5" />;
    if (type.startsWith('video/')) return <Video className="h-5 w-5" />;
    if (type.startsWith('audio/')) return <Music className="h-5 w-5" />;
    return <FileText className="h-5 w-5" />;
  };

  const isImage = (attachment.mime_type || attachment.file_type).startsWith('image/');
  const isVideo = (attachment.mime_type || attachment.file_type).startsWith('video/');
  const isAudio = (attachment.mime_type || attachment.file_type).startsWith('audio/');

  return (
    <div
      className={`relative rounded-lg overflow-hidden ${
        theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'
      }`}
    >
      {showRemove && onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-2 right-2 z-10 p-1 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {isImage && attachment.storage_url ? (
        <div className="relative group cursor-pointer" onClick={() => setShowModal(true)}>
          <img
            src={attachment.storage_url}
            alt={attachment.file_name}
            className="max-w-xs max-h-64 rounded-lg object-cover"
            loading="lazy"
          />
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
            <Maximize2 className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          </div>
          {/* Download button */}
          <a
            href={attachment.storage_url}
            download={attachment.file_name}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-2 right-2 p-2 rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 text-white transition-all opacity-0 group-hover:opacity-100"
          >
            <Download className="h-4 w-4" />
          </a>
        </div>
      ) : isVideo && attachment.storage_url ? (
        <div className="max-w-xs">
          <video
            src={attachment.storage_url}
            controls
            className="max-h-64 rounded-lg"
            preload="metadata"
          >
            Your browser does not support video playback.
          </video>
          <div className={`p-2 text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            {attachment.file_name}
          </div>
        </div>
      ) : isAudio && attachment.storage_url ? (
        <div className="p-3 space-y-2">
          <div className="flex items-center gap-2">
            <Music className="h-5 w-5 text-blue-500" />
            <span
              className={`text-sm font-medium ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
              }`}
            >
              {attachment.file_name}
            </span>
          </div>
          <audio src={attachment.storage_url} controls className="w-full">
            Your browser does not support audio playback.
          </audio>
          <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            {formatFileSize(attachment.file_size)}
          </span>
        </div>
      ) : (
        <div className="p-3 flex items-center gap-2 w-full">
          <div
            className={`p-2 rounded flex-shrink-0 ${
              theme === 'dark' ? 'bg-gray-600' : 'bg-gray-200'
            }`}
          >
            {getFileIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className={`text-sm font-medium truncate ${
                theme === 'dark' ? 'text-gray-200' : 'text-gray-800'
              }`}
              title={attachment.file_name}
            >
              {attachment.file_name.length > 30
                ? `${attachment.file_name.substring(0, 27)}...`
                : attachment.file_name}
            </p>
            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              {formatFileSize(attachment.file_size)}
            </p>
          </div>
          {attachment.storage_url && (
            <a
              href={attachment.storage_url}
              download={attachment.file_name}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-2 rounded transition-colors flex-shrink-0 ${
                theme === 'dark' ? 'hover:bg-gray-600' : 'hover:bg-gray-300'
              }`}
              title="Download"
            >
              <Download className="h-4 w-4" />
            </a>
          )}
        </div>
      )}

      {/* Image Preview Modal */}
      {showModal &&
        isImage &&
        attachment.storage_url &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setShowModal(false)}
          >
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
            <a
              href={attachment.storage_url}
              download={attachment.file_name}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-4 right-16 p-2 rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 text-white transition-colors"
            >
              <Download className="h-6 w-6" />
            </a>
            <div className="max-w-7xl max-h-full" onClick={(e) => e.stopPropagation()}>
              <img
                src={attachment.storage_url}
                alt={attachment.file_name}
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
              />
              <div className="mt-4 text-center text-white">
                <p className="font-medium">{attachment.file_name}</p>
                <p className="text-sm text-gray-300">{formatFileSize(attachment.file_size)}</p>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
